import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type AdminReportItem } from '@/services/api';

export default function AdminReportsPage() {
  const [reports, setReports] = useState<AdminReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [selectedReport, setSelectedReport] = useState<AdminReportItem | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminApi
      .listReports({ status: filter, page: 1, pageSize: 100 })
      .then((res) => setReports(res.list))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = (id: number, action: 'resolve' | 'reject') => {
    adminApi.handleReport(id, action).then(fetchData).catch(() => {});
  };

  const statusLabel = (s: string) => (s === 'pending' ? '待处理' : s === 'resolved' ? '已处理' : '已驳回');
  const statusClass = (s: string) =>
    s === 'pending' ? 'bg-amber-100 text-amber-700' : s === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-background-200 text-foreground-500';

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">举报管理</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            待处理 {reports.filter((r) => r.status === 'pending').length} 条
          </p>
        </div>

        <div className="flex items-center gap-2">
          {['', 'pending', 'resolved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filter === s ? 'bg-foreground-950 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {s === '' ? '全部' : statusLabel(s)}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">举报人</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">被举报对象</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden sm:table-cell">原因</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">状态</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-10 text-foreground-400">加载中...</td></tr>
                ) : reports.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-foreground-400">暂无举报</td></tr>
                ) : (
                  reports.map((r) => (
                    <tr key={r.id} className="border-b border-background-100 hover:bg-background-50/50">
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{r.reporterName || '匿名'}</td>
                      <td className="px-4 py-3 text-foreground-900 font-medium max-w-[200px] truncate">
                        {r.targetTitle || '--'}
                        <span className="ml-1.5 text-xs text-foreground-400">({r.targetType === 'supply' ? '供给' : '需求'})</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 hidden sm:table-cell max-w-[240px] truncate">{r.reason}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(r.status)}`}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleAction(r.id, 'resolve')}
                              className="px-3 py-1 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              处理
                            </button>
                            <button
                              onClick={() => handleAction(r.id, 'reject')}
                              className="px-3 py-1 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              驳回
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setSelectedReport(r)}
                            className="px-2.5 py-1 rounded-md text-xs text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            查看
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {selectedReport && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={() => setSelectedReport(null)} />
            <div className="relative bg-white rounded-xl p-5 w-full max-w-sm border border-background-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">举报详情</h3>
                <button onClick={() => setSelectedReport(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-foreground-500">举报人</span><span className="text-foreground-900 font-medium">{selectedReport.reporterName || '匿名'}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">被举报对象</span><span className="text-foreground-900">{selectedReport.targetTitle || '--'}</span></div>
                <div><span className="text-foreground-500 block mb-1">原因</span><span className="text-foreground-900">{selectedReport.reason}</span></div>
                {selectedReport.detail && (
                  <div><span className="text-foreground-500 block mb-1">详情</span><span className="text-foreground-900">{selectedReport.detail}</span></div>
                )}
                {selectedReport.note && (
                  <div><span className="text-foreground-500 block mb-1">处理备注</span><span className="text-foreground-900">{selectedReport.note}</span></div>
                )}
                <div className="flex justify-between"><span className="text-foreground-500">提交时间</span><span className="text-foreground-900">{new Date(selectedReport.createdAt).toLocaleString('zh-CN')}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">状态</span><span className="font-medium">{statusLabel(selectedReport.status)}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
